type PollVoterResponse = {
  userId: string;
  optionId: string;
  respondedAt: string;
  voterName?: string;
  voterEmail?: string;
  canonicalUserId?: string;
};

type AudienceMember = { id: string; name: string; email: string };

type ViewerHint = { id?: string; name: string; email: string };

function normalizeEmail(email: string | undefined): string {
  return (email ?? "").trim().toLowerCase();
}

function resolveMemberName(
  vote: PollVoterResponse,
  audienceById: Map<string, AudienceMember>,
  audienceByEmail: Map<string, AudienceMember>,
  hintsById: Map<string, ViewerHint>,
  hintsByEmail: Map<string, ViewerHint>
): PollVoterResponse {
  const unknown =
    !vote.voterName || vote.voterName === "Unknown voter";
  if (!unknown) {
    return vote;
  }

  const idsToTry = [vote.canonicalUserId, vote.userId].filter(Boolean) as string[];
  for (const id of idsToTry) {
    const fromAudience = audienceById.get(id);
    if (fromAudience) {
      return {
        ...vote,
        voterName: fromAudience.name,
        voterEmail: vote.voterEmail || fromAudience.email,
      };
    }
    const fromHint = hintsById.get(id);
    if (fromHint) {
      return {
        ...vote,
        voterName: fromHint.name,
        voterEmail: vote.voterEmail || fromHint.email,
      };
    }
  }

  const emailKey = normalizeEmail(vote.voterEmail);
  if (emailKey) {
    const fromAudience = audienceByEmail.get(emailKey);
    if (fromAudience) {
      return {
        ...vote,
        voterName: fromAudience.name,
        voterEmail: fromAudience.email,
      };
    }
    const fromHint = hintsByEmail.get(emailKey);
    if (fromHint) {
      return {
        ...vote,
        voterName: fromHint.name,
        voterEmail: fromHint.email,
      };
    }
  }

  return vote;
}

export function enrichPollVotersWithAudience<
  T extends PollVoterResponse,
>(
  responses: T[],
  audienceMembers: AudienceMember[],
  viewerHints: ViewerHint[] = []
): T[] {
  const audienceById = new Map(
    audienceMembers.map((member) => [member.id, member])
  );
  const audienceByEmail = new Map(
    audienceMembers
      .filter((member) => normalizeEmail(member.email))
      .map((member) => [normalizeEmail(member.email), member])
  );
  const hintsById = new Map(
    viewerHints
      .filter((hint) => hint.id)
      .map((hint) => [hint.id as string, hint])
  );
  const hintsByEmail = new Map(
    viewerHints
      .filter((hint) => normalizeEmail(hint.email))
      .map((hint) => [normalizeEmail(hint.email), hint])
  );

  return responses.map((vote) =>
    resolveMemberName(
      vote,
      audienceById,
      audienceByEmail,
      hintsById,
      hintsByEmail
    ) as T
  );
}

export function collectPollVoterUserIds(
  responses: Array<{ userId: string; canonicalUserId?: string }>
): Set<string> {
  const ids = new Set<string>();
  for (const response of responses) {
    ids.add(response.userId);
    if (response.canonicalUserId) {
      ids.add(response.canonicalUserId);
    }
  }
  return ids;
}
